import React, { FunctionComponent } from 'react'
import { vn } from 'vend-number'
import { useT } from '@transifex/react'
import { useAppSelector } from '../../../state/hooks'
import { selectSaleDiscount } from '../../../state/selectors'
import { useRegisterManager, useSaleManager } from '../../../ng-compat/hooks'
import {
  selectSaleFinals,
  selectSaleTotals,
  selectSaleTotalsServiceFee,
  selectLspCashDiscountPercentage,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { useTranslatedSurchargePaymentLabel } from '../sale-payment/useSurchargePaymentLabel'
import { useFormatPercentage } from '../../helpers/formatNumberHooks'
import { SaleSubtotal } from './Subtotal'
import { SaleDiscountDisplay } from './SaleDiscount'
import { SaleTax } from './SaleTax'
import { useFormatCurrencyValue } from './useFormatCurrencyValue'
import { SubtotalEcomCustomCharges } from './SubtotalEcomCustomCharges'

export const SubtotalSection: FunctionComponent = () => {
  const saleDiscount = useAppSelector(selectSaleDiscount)
  const registerManager = useRegisterManager()
  const saleManager = useSaleManager()
  const isTaxInclusive = registerManager.isTaxInclusive()
  const saleFinals = useAppSelector(selectSaleFinals)
  const saleTotals = useAppSelector(selectSaleTotals)
  const serviceFeeTotals = useAppSelector(selectSaleTotalsServiceFee)
  const formatCurrencyValue = useFormatCurrencyValue()
  const formatPercentage = useFormatPercentage()
  const hasSurcharge = !vn(saleFinals.surchargeDisplayProperties.surcharge).eq(
    0
  )
  const lspCashDiscountPercentage = useAppSelector(
    selectLspCashDiscountPercentage
  )
  const hasLspCashDiscount =
    lspCashDiscountPercentage > 0 && !vn(saleTotals.lspCashDiscount).eq(0)
  const surchargePaymentLabel = useTranslatedSurchargePaymentLabel()
  const t = useT()
  return (
    <ul className="sale-summary-list">
      {!isTaxInclusive && <SaleSubtotal />}

      {saleDiscount && <SaleDiscountDisplay />}

      {/* isTaxInclusive is boolean. So if the retailer is tax exclusive then this won't get rendered. Instead subtotal is gonna be rendered above sale discount */}
      {isTaxInclusive && <SaleSubtotal />}

      {hasLspCashDiscount ? (
        <SubtotalSectionRow
          name={t('Cash discount {rate}', {
            _context: 'LSPay cash discount line in sale summary',
            rate: formatPercentage(lspCashDiscountPercentage / 100),
          })}
          amount={formatCurrencyValue(saleTotals.lspCashDiscount)}
          key="lsp-cash-discount-subtotal-row"
        />
      ) : null}

      <SaleTax />

      {saleManager.hasCashDiscountingServiceFee() ? (
        <SubtotalSectionRow
          name={t('Service fee', {
            _context: 'Service Fee in sale receipt preview',
          })}
          amount={formatCurrencyValue(serviceFeeTotals)}
          key="Service-fee-subtotal-row"
        />
      ) : null}

      {vn(saleFinals.tip).gt(0) ? (
        <SubtotalSectionRow
          name={t('Tip', {
            _context: 'Tip label in sale receipt preview',
          })}
          amount={formatCurrencyValue(saleFinals.tip)}
          key="tip-fee-subtotal-row"
        />
      ) : null}

      {hasSurcharge ? (
        <SubtotalSectionRow
          name={surchargePaymentLabel}
          amount={formatCurrencyValue(
            saleFinals.surchargeDisplayProperties.surcharge
          )}
          key="surcharge-subtotal-row"
        />
      ) : null}

      <SubtotalEcomCustomCharges />
    </ul>
  )
}

type SubtotalSectionRowProps = { name: string; amount: string }

const SubtotalSectionRow: FunctionComponent<SubtotalSectionRowProps> = ({
  name,
  amount,
}: SubtotalSectionRowProps) => {
  return (
    <li className="sale-summary-line vd-pt2">
      <div className="sale-summary-line-item sale-summary-line-item--label">
        {name}
      </div>
      <div className="sale-summary-line-item sale-summary-line-item--value">
        {amount}
      </div>
    </li>
  )
}
